import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth, startGoogleLogin } from "@/context/CustomerAuthContext";
import { NAV } from "@/constants/testIds";
import VeteranBadge from "@/components/VeteranBadge";

export default function Header() {
  const { count } = useCart();
  const { user } = useCustomerAuth();
  const [open, setOpen] = useState(false);

  const linkBase = "text-sm tracking-[0.14em] uppercase font-sans font-light transition-colors";
  const linkActive = "text-[#1A1C1A]";
  const linkIdle = "text-stone-500 hover:text-[#1A1C1A]";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#F9F9F6]/85 backdrop-blur-xl backdrop-saturate-150 border-b border-stone-200/60">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.brandLink}
          className="font-serif text-2xl md:text-3xl tracking-tight text-[#1A1C1A] font-light inline-flex items-center"
        >
          Jenova <span className="italic font-light text-stone-500">Collective</span>
          <VeteranBadge variant="inline" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink to="/shop" data-testid={NAV.shopLink} className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}>
            Shop
          </NavLink>
          <NavLink to="/shop?category=gym" data-testid={NAV.gymLink} className={`${linkBase} ${linkIdle}`}>
            Performance
          </NavLink>
          <NavLink to="/shop?category=beauty" data-testid={NAV.beautyLink} className={`${linkBase} ${linkIdle}`}>
            Beauty
          </NavLink>
          <NavLink to="/about" data-testid={NAV.aboutLink} className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}>
            Our Story
          </NavLink>
          <NavLink to="/experience" data-testid="nav-community-link" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}>
            Community
          </NavLink>
        </nav>

        <div className="flex items-center gap-2 md:gap-4">
          {user ? (
            <Link
              to="/account"
              data-testid="nav-account-link"
              className="p-2 text-[#1A1C1A] hover:opacity-70 transition-opacity"
              aria-label="Account"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <User strokeWidth={1.25} className="w-5 h-5" />
              )}
            </Link>
          ) : (
            <button
              onClick={() => startGoogleLogin("/account")}
              data-testid="nav-signin-button"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-sans font-light text-stone-600 hover:text-[#1A1C1A] transition-colors px-2 py-2"
            >
              <User strokeWidth={1.5} className="w-4 h-4" /> Sign in
            </button>
          )}
          <Link
            to="/cart"
            data-testid={NAV.cartLink}
            className="relative p-2 text-[#1A1C1A] hover:opacity-70 transition-opacity"
            aria-label="Cart"
          >
            <ShoppingBag strokeWidth={1.25} className="w-5 h-5" />
            {count > 0 && (
              <span
                data-testid={NAV.cartCount}
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] text-[10px] font-medium bg-[#1A1C1A] text-[#F9F9F6] rounded-full flex items-center justify-center px-1"
              >
                {count}
              </span>
            )}
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 text-[#1A1C1A]"
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X strokeWidth={1.25} className="w-5 h-5" /> : <Menu strokeWidth={1.25} className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-stone-200/60 bg-[#F9F9F6]">
          <div className="px-6 py-6 flex flex-col gap-5">
            <Link to="/shop" onClick={() => setOpen(false)} className={`${linkBase} ${linkIdle}`}>Shop</Link>
            <Link to="/shop?category=gym" onClick={() => setOpen(false)} className={`${linkBase} ${linkIdle}`}>Performance</Link>
            <Link to="/shop?category=beauty" onClick={() => setOpen(false)} className={`${linkBase} ${linkIdle}`}>Beauty</Link>
            <Link to="/about" onClick={() => setOpen(false)} className={`${linkBase} ${linkIdle}`}>Our Story</Link>
            <Link to="/experience" onClick={() => setOpen(false)} data-testid="nav-mobile-community-link" className={`${linkBase} ${linkIdle}`}>Community</Link>
            <Link to="/my-jenova" onClick={() => setOpen(false)} data-testid="nav-mobile-my-jenova-link" className={`${linkBase} ${linkIdle}`}>My Jenova</Link>
          </div>
        </div>
      )}
    </header>
  );
}
